/** Opens the notification editor at a specific template line.
 *
 *  `?dm=<ndm id>&field=<column>&line=<n>` — the counterpart to `useEditIntent`,
 *  and what makes a line number in the Anatomy viewer actually navigable.
 *
 *  ## Why this is a hook and not a one-liner
 *
 *  Four things have to happen, in order, before a line can be revealed:
 *
 *  1. The page selects that delivery method — the editor renders one
 *     `DeliveryMethodEditor` at a time.
 *  2. That method's per-field accordion opens; `expanded` is local state inside
 *     `CustomTemplateAccordion` and defaults to closed for an empty field.
 *  3. `TemplateCodeEditor` mounts and publishes its imperative handle.
 *  4. **The editor core — a lazy chunk — finishes importing.** Until then every
 *     method on that handle is a silent no-op, which is why the handle exposes
 *     `isReady()`: without it a caller cannot tell a successful reveal from
 *     nothing happening at all. This was the actual bug in the first cut.
 *
 *  So the intent is *held* and retried rather than fired once, and the registry
 *  stores each accordion's **ref object** rather than a snapshot of its
 *  `.current` — a snapshot taken during the parent's mount effect is null while
 *  the chunk loads, and nothing would ever re-register it.
 *
 *  The intent is consumed on success (history `replace`, other params kept) so
 *  it cannot re-fire or add a back-button step. If it cannot be resolved at all
 *  — no such delivery method, or a field this channel does not have —
 *  `unresolved` goes true after the grace period so the page can say so; a
 *  silently ignored link is indistinguishable from a broken feature.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { TemplateCodeEditorHandle } from '@/components/TemplateCodeEditor';

/** How long to keep waiting for the lazy editor chunk before giving up. */
const REVEAL_TIMEOUT_MS = 8000;
const RETRY_MS = 60;

export interface RevealIntent {
  dmId: number;
  field: string;
  line: number | null;
}

export type EditorHandleRef = MutableRefObject<TemplateCodeEditorHandle | null>;
/** `${dmId}.${field}` → that field's editor ref (not a snapshot of it). */
export type EditorRegistry = Map<string, EditorHandleRef>;

export function revealKey(dmId: number, field: string): string {
  return `${dmId}.${field}`;
}

export interface UseRevealIntentResult {
  /** The pending intent, or null. Accordions read it to force themselves open. */
  intent: RevealIntent | null;
  /** Called by each `CustomTemplateAccordion` with its editor ref. */
  register(key: string, ref: EditorHandleRef | null): void;
  /** The intent could not be resolved — surface it rather than doing nothing. */
  unresolved: boolean;
}

export function useRevealIntent(onSelectDm: (dmId: number) => void): UseRevealIntentResult {
  const [params, setParams] = useSearchParams();
  const [unresolved, setUnresolved] = useState(false);

  const dmRaw = params.get('dm');
  const field = params.get('field');
  const lineRaw = params.get('line');

  const intent = useMemo<RevealIntent | null>(() => {
    if (!dmRaw || !field || !/^\d+$/.test(dmRaw)) return null;
    return {
      dmId: Number(dmRaw),
      field,
      line: lineRaw && /^\d+$/.test(lineRaw) ? Number(lineRaw) : null,
    };
  }, [dmRaw, field, lineRaw]);

  const registry = useRef<EditorRegistry>(new Map());
  const done = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const deadline = useRef<number>(0);

  // Latest values for the retry loop, which must not be rebuilt on every render
  // (a new closure each tick would restart the deadline).
  const intentRef = useRef(intent);
  intentRef.current = intent;
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  /** Attempt the reveal; reschedule while the editor core is still loading. */
  const attempt = useCallback(() => {
    timer.current = null;
    const current = intentRef.current;
    if (!current) return;

    const key = revealKey(current.dmId, current.field);
    if (done.current === key) return;

    const ref = registry.current.get(key);
    const handle = ref?.current ?? null;

    if (handle && handle.isReady()) {
      done.current = key;
      if (current.line) handle.revealLine(current.line);
      else handle.focus();
      const next = new URLSearchParams(paramsRef.current);
      ['dm', 'field', 'line'].forEach((k) => next.delete(k));
      setParamsRef.current(next, { replace: true });
      return;
    }

    if (Date.now() < deadline.current) {
      timer.current = window.setTimeout(attempt, RETRY_MS);
      return;
    }
    // Out of time: either the field does not exist on this notification or the
    // editor never loaded. Either way, say so.
    setUnresolved(true);
  }, []);

  const register = useCallback(
    (key: string, ref: EditorHandleRef | null) => {
      if (ref) registry.current.set(key, ref);
      else registry.current.delete(key);
      // The editor we were waiting for may have just arrived.
      attempt();
    },
    [attempt],
  );

  // A new intent restarts the whole sequence: select the delivery method (step
  // 1 — nothing for it can mount before that), then begin attempting.
  useEffect(() => {
    clearTimer();
    if (!intent) return undefined;
    done.current = null;
    setUnresolved(false);
    deadline.current = Date.now() + REVEAL_TIMEOUT_MS;
    onSelectDm(intent.dmId);
    attempt();
    return clearTimer;
    // Keyed on the intent identity: re-running on every render would fight the
    // user's own delivery-method clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intent?.dmId, intent?.field, intent?.line]);

  return { intent, register, unresolved };
}
